/* Transferhistorie eines Spielers: Liste und Änderungen.
 *
 * Gemeinsamer Zustand je Spieler (Modulbereich, wie die anderen geteilten
 * Composables): PlayerPanel.vue braucht beim Öffnen nur zu wissen, OB es Einträge
 * gibt (Reiter-Regel, domain/playerTabs.js), TransferHistory.vue zeigt sie an.
 * Beide benutzen dieselbe Liste und dieselbe Abfrage:
 *   - PlayerPanel lädt beim Öffnen eines Spielers frisch (`refresh: true`),
 *   - TransferHistory nutzt danach die geladene Liste (keine zweite Abfrage),
 *     eine gleichzeitig laufende Abfrage wird mitbenutzt.
 * Legt jemand den ersten Eintrag an oder löscht den letzten, ändert sich die
 * gemeinsame Liste sofort — der Reiter erscheint bzw. verschwindet ohne Neuladen.
 *
 * Das Schreiben läuft NICHT über den Speichern-Knopf des Panels
 * (PlayerPanel.onSave), sondern sofort und einzeln über die Funktionen hier — eine
 * Zeile in player_transfers ist ein eigener Datensatz, nicht Teil von players.data.
 *
 * add/update/remove liefern true bei Erfolg. Bei einem Fehler false, und `error`
 * trägt den allgemeinen Schlüssel (errors.<schlüssel> in den Sprachdateien);
 * Details stehen in der Konsole (supabase/transfers.js).
 *
 * Der Speicher selbst steht in transferStore.js (testbar ohne Datenbank).
 */

import { ref, computed, watch, toValue } from 'vue';
import {
  listTransfers, insertTransfer, updateTransfer, deleteTransfer,
} from '../supabase/transfers.js';
import { transferLogoPaths } from '../domain/transfers.js';
import { receivingTransfer } from '../domain/playerListExport.js';
import { createTransferStore } from './transferStore.js';

/**
 * Logo-Pfade der Historie eines Spielers, frisch aus der Datenbank. Dient dem
 * Aufräumen nach dem Speichern (PlayerPanel.onSave -> fileCleanup `keep`).
 * Wirft bei Fehler; der Aufrufer löscht dann nichts.
 */
export async function fetchTransferLogoPaths(playerId) {
  return transferLogoPaths(await listTransfers(playerId));
}

const store = createTransferStore({
  list: listTransfers,
  insert: insertTransfer,
  update: updateTransfer,
  remove: deleteTransfer,
});

/**
 * Die Historie eines Spielers für die Player Card: dieselbe Liste wie im Reiter Transfer Info (gemeinsamer Speicher,
 * dieselbe Abfrage, dieselbe Rollen-Sicht). Schon geladene Listen werden nicht erneut geholt. Schlägt das Laden
 * fehl, ist die Liste leer — die Karte entsteht dann ohne Block.
 */
export async function loadTransfersFor(playerId) {
  await store.load(playerId);
  return store.itemsOf(playerId);
}

/** Die bisher geladene Historie (ohne Abfrage; reaktiv) — für die Vorschau im Reiter Material. */
export const transfersOf = (playerId) => store.itemsOf(playerId);

/**
 * @param {string|import('vue').Ref<string>|(() => string)} playerId
 * @param {{ refresh?: boolean }} [options] refresh: beim Wechsel des Spielers immer frisch
 *        abfragen (PlayerPanel); sonst die schon geladene Liste benutzen (TransferHistory)
 */
export function useTransfers(playerId, { refresh = false } = {}) {
  const items = computed(() => store.itemsOf(toValue(playerId)));
  const loading = computed(() => store.isLoading(toValue(playerId)));
  const busy = ref(false);
  const error = ref(null);

  async function load(options = {}) {
    const id = toValue(playerId);
    error.value = null;
    await store.load(id, { force: refresh, ...options });
    if (id === toValue(playerId)) error.value = store.errorOf(id);
  }

  watch(() => toValue(playerId), () => load(), { immediate: true });

  async function run(fn) {
    busy.value = true;
    error.value = null;
    try {
      await fn();
      return true;
    } catch (e) {
      error.value = e.message || 'saveFailed';
      return false;
    } finally {
      busy.value = false;
    }
  }

  const add = (transfer) => run(() => store.add(toValue(playerId), transfer));
  const update = (id, transfer) => run(() => store.update(toValue(playerId), id, transfer));
  const remove = (id) => run(() => store.remove(toValue(playerId), id));

  return { items, loading, busy, error, load, add, update, remove };
}

/**
 * Transferdatum je Spieler für die Transferred-Seite (Sortierung). Lädt die Historie der übergebenen
 * Spieler in den gemeinsamen Speicher (je Spieler eine Abfrage, schon geladene werden nicht erneut geholt —
 * dieselbe Quelle wie der Export, domain/playerListExport.js receivingTransfer). Solange eine Historie
 * fehlt oder fehlschlägt, gilt der Spieler als "ohne Datum" und steht hinten.
 *
 * @param {import('vue').Ref<Array>|(() => Array)} players
 * @returns {{ dateOf: (player: object) => string }} 'JJJJ-MM-TT' oder ''
 */
export function useTransferDates(players) {
  watch(
    () => toValue(players).map((p) => p.id).join('|'),
    () => toValue(players).forEach((p) => store.load(p.id)),
    { immediate: true }
  );
  const dateOf = (player) => receivingTransfer(player, store.itemsOf(player.id)).date;
  return { dateOf };
}
